import React, { useEffect } from 'react';
import Layout from '../components/layout/Layout';
import CTASection from '../components/shared/CTASection';
import { FadeInOnScroll } from '../components/shared/Parallax';
import { Shield, Award } from 'lucide-react';

// Video YouTube IDs
const videos = [
  { id: '-odCJbhEnes', title: 'Il Nostro Laboratorio' },
  { id: 'IwPeUrd-m8I', title: 'Qualità dei Materiali' },
  { id: 'WLIX2qEX7Ss', title: 'Il Nostro Team' }
];

// Video embed con autoplay, loop, senza controlli - scalato per nascondere branding YouTube
const YouTubeEmbed = ({ videoId, title }) => (
  <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-lg bg-gray-900">
    <div className="absolute -inset-[18%]">
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0`}
        title={title}
        className="w-full h-full pointer-events-none"
        style={{ border: 'none' }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      />
    </div>
  </div>
);

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      {/* Hero Section with YouTube Video */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center justify-center overflow-hidden bg-gray-900">
        {/* YouTube Video Background - scalato per nascondere branding */}
        <div className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden">
          <div className="absolute -inset-[20%]">
            <iframe
              src="https://www.youtube-nocookie.com/embed/WLIX2qEX7Ss?autoplay=1&mute=1&loop=1&playlist=WLIX2qEX7Ss&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0"
              title="Giovanni Tende - Chi Siamo"
              className="w-full h-full"
              style={{ border: 'none' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        </div>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6 leading-tight">
            Produzione propria da oltre 60 anni
          </h1>
          <p className="text-lg md:text-xl text-white/90 font-light mb-8 max-w-2xl mx-auto">
            Dal taglio del tessuto al montaggio finale, ogni fase viene realizzata internamente nei nostri reparti a Pisa.
          </p>
          <a 
            href="/preventivo" 
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white px-8 py-4 text-lg font-medium transition-colors"
          >
            Richiedi un preventivo
          </a>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* Main Content - 3 Paragrafi con Video */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="text-center mb-16">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                La Nostra Produzione
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight">
                Dove nascono le nostre creazioni
              </h2>
            </div>
          </FadeInOnScroll>

          {/* Paragrafo 1 - Il Laboratorio */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 lg:mb-28">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  Il Nostro Laboratorio
                </span>
                <h3 className="text-2xl md:text-3xl font-light text-gray-900 leading-tight mb-6">
                  Produzione 100% interna
                </h3>
                <p className="text-gray-600 font-light leading-relaxed text-lg">
                  Tutto viene realizzato nel nostro stabilimento a Pisa: dalla lavorazione 
                  delle strutture alla cucitura dei tessuti. Non siamo rivenditori, siamo 
                  produttori diretti con un laboratorio completo dove ogni prodotto prende 
                  forma dalle nostre mani. Questo ci permette di garantire qualità, 
                  personalizzazione e tempi di consegna ottimali.
                </p>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <YouTubeEmbed videoId={videos[0].id} title={videos[0].title} />
            </FadeInOnScroll>
          </div>

          {/* Paragrafo 2 - I Materiali */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 lg:mb-28">
            <FadeInOnScroll delay={200} className="order-2 lg:order-1">
              <YouTubeEmbed videoId={videos[1].id} title={videos[1].title} />
            </FadeInOnScroll>
            <FadeInOnScroll className="order-1 lg:order-2">
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  Qualità dei Materiali
                </span>
                <h3 className="text-2xl md:text-3xl font-light text-gray-900 leading-tight mb-6">
                  Solo il meglio per i nostri clienti
                </h3>
                <p className="text-gray-600 font-light leading-relaxed text-lg">
                  Utilizziamo esclusivamente materiali certificati e di alta qualità. 
                  Tessuti Tempotest Parà, alluminio verniciato a polvere, componentistica 
                  di prima scelta. Il nostro magazzino interno ci permette di avere sempre 
                  disponibilità immediata e di ridurre drasticamente i tempi di attesa 
                  per i nostri clienti.
                </p>
              </div>
            </FadeInOnScroll>
          </div>

          {/* Paragrafo 3 - Il Team */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  Il Nostro Team
                </span>
                <h3 className="text-2xl md:text-3xl font-light text-gray-900 leading-tight mb-6">
                  Personale interno qualificato
                </h3>
                <p className="text-gray-600 font-light leading-relaxed text-lg">
                  Dal preventivo all'installazione, tutto viene gestito dal nostro team interno. 
                  Non subappaltiamo mai: i nostri montatori sono dipendenti qualificati con 
                  anni di esperienza. Questo significa un unico interlocutore, maggiore 
                  controllo sulla qualità e assistenza diretta in ogni fase del progetto.
                </p>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <YouTubeEmbed videoId={videos[2].id} title={videos[2].title} />
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Sezione Certificazioni */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Certificazioni
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-6">
                Qualifica e professionalità riconosciute
              </h2>
              <p className="text-gray-600 font-light leading-relaxed text-lg">
                Siamo azienda associata Assites e iscritti all'Albo Professionale Protezioni Solari.
              </p>
            </div>
          </FadeInOnScroll>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <FadeInOnScroll delay={100}>
              <div className="bg-white p-8 lg:p-10 text-center border border-gray-100 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-sky-500/10 flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-8 h-8 text-sky-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Albo Professionale Protezioni Solari
                </h3>
                <p className="text-gray-500 font-light">
                  Produttore Qualificato N° 4942
                </p>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <div className="bg-white p-8 lg:p-10 text-center border border-gray-100 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-sky-500/10 flex items-center justify-center mx-auto mb-6">
                  <Award className="w-8 h-8 text-sky-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  ASSITES
                </h3>
                <p className="text-gray-500 font-light">
                  Associazione Italiana Tende da Sole<br />Azienda Associata
                </p>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Sezione Finale - Valori */}
      <section className="py-20 lg:py-28 bg-gray-900 text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-400">
                I Nostri Valori
              </span>
              <h2 className="text-3xl md:text-4xl font-light leading-tight mb-8">
                Esperienza, qualità, continuità
              </h2>
              <p className="text-gray-300 font-light leading-relaxed text-lg">
                Affidabilità nel tempo, materiali selezionati e competenza specifica maturata 
                in oltre 60 anni nel settore. Produzione interna, controllo diretto e rapporto 
                trasparente con il cliente: questo è il nostro modo di lavorare.
              </p>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Photo Collage Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <FadeInOnScroll>
            <div className="text-center mb-12">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Il Nostro Lavoro
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900">
                Passione e precisione in ogni dettaglio
              </h2>
            </div>
          </FadeInOnScroll>
          
          {/* Collage Grid - 9 images layout */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 max-w-5xl mx-auto">
            {/* Row 1 */}
            <FadeInOnScroll delay={100}>
              <div className="relative h-[200px] md:h-[280px] overflow-hidden rounded-lg group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/q3fffw53_immagine%20cs%201%20.png"
                  alt="Lavorazione tende - Giovanni Tende"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={150}>
              <div className="relative h-[200px] md:h-[280px] overflow-hidden rounded-lg group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/wbleg2p5_imm%20cs%202%20.png"
                  alt="Cucitura tessuti - Giovanni Tende"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={200}>
              <div className="relative h-[200px] md:h-[280px] overflow-hidden rounded-lg group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/wr5ic9zs_imm%20cs%203%20.png"
                  alt="Tenda finita - Giovanni Tende"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
            
            {/* Row 2 */}
            <FadeInOnScroll delay={250}>
              <div className="relative h-[200px] md:h-[280px] overflow-hidden rounded-lg group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/rcz8zh6q_imm%20cs%204%20.png"
                  alt="Magazzino tessuti - Giovanni Tende"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={300}>
              <div className="relative h-[200px] md:h-[280px] overflow-hidden rounded-lg group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/xs1shzmc_imm%20cs%206.png"
                  alt="Taglio tessuti - Giovanni Tende"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={350}>
              <div className="relative h-[200px] md:h-[280px] overflow-hidden rounded-lg group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/mndoo7wf_imm%20cs%207.png"
                  alt="Taglio tessuto - Giovanni Tende"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
            
            {/* Row 3 */}
            <FadeInOnScroll delay={400}>
              <div className="relative h-[200px] md:h-[280px] overflow-hidden rounded-lg group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/dl1vmz6m_imm%20cs%208.png"
                  alt="Cucitura professionale - Giovanni Tende"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={450}>
              <div className="relative h-[200px] md:h-[280px] overflow-hidden rounded-lg group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/xuthfr3x_imm%20cs%209.png"
                  alt="Taglio profilati - Giovanni Tende"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={500}>
              <div className="relative h-[200px] md:h-[280px] overflow-hidden rounded-lg group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/78rpqt9a_imm%20cs%2010.png"
                  alt="Lavorazione strutture - Giovanni Tende"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Vieni a trovarci"
        description="Scopri di persona i nostri reparti e la qualità del nostro lavoro."
        variant="light"
      />
    </Layout>
  );
};

export default AboutPage;
